import React, { useMemo } from 'react';
import { Button, ListGroup } from 'react-bootstrap';
import { OpportunityFolderTreeNode } from '../types';
import './OpportunityFolderTree.css';

function findFolderPath(
  nodes: OpportunityFolderTreeNode[],
  targetId: number | null | undefined
): OpportunityFolderTreeNode[] | null {
  if (targetId == null) return null;
  for (const node of nodes) {
    if (node.id === targetId) {
      return [node];
    }
    const childPath = findFolderPath(node.children, targetId);
    if (childPath) {
      return [node, ...childPath];
    }
  }
  return null;
}

interface OpportunityFolderTreeProps {
  folders: OpportunityFolderTreeNode[];
  selectedFolderId?: number | null;
  rootLabel?: string;
  canEdit?: boolean;
  searchTerm?: string;
  onSelectFolder: (folderId: number | null) => void;
  onCreateFolder: (parentFolderId?: number | null) => void;
  onApplyModuleDefaults?: (parentFolderId: number | null) => void;
  onRenameFolder: (folder: OpportunityFolderTreeNode) => void;
  onMoveFolder: (folder: OpportunityFolderTreeNode) => void;
  onDeleteFolder: (folder: OpportunityFolderTreeNode) => void;
}

const OpportunityFolderTree: React.FC<OpportunityFolderTreeProps> = ({
  folders,
  selectedFolderId,
  rootLabel = 'Raiz da oportunidade',
  canEdit = false,
  searchTerm = '',
  onSelectFolder,
  onCreateFolder,
  onApplyModuleDefaults,
  onRenameFolder,
  onMoveFolder,
  onDeleteFolder,
}) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [openMenuId, setOpenMenuId] = React.useState<number | null>(null);
  const normalize = (value: string) => value.toLowerCase().trim();
  const searchNormalized = normalize(searchTerm);

  React.useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current) return;
      if (event.target instanceof Node && !containerRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const filterTree = (nodes: OpportunityFolderTreeNode[]): OpportunityFolderTreeNode[] => {
    if (!searchNormalized) return nodes;
    const filtered: OpportunityFolderTreeNode[] = [];
    for (const node of nodes) {
      const childMatches = filterTree(node.children);
      const selfMatches = normalize(node.name).includes(searchNormalized);
      if (selfMatches || childMatches.length > 0) {
        filtered.push({ ...node, children: childMatches });
      }
    }
    return filtered;
  };

  const visibleFolders = filterTree(folders);

  const selectedPath = useMemo(
    () => findFolderPath(folders, selectedFolderId),
    [folders, selectedFolderId]
  );
  const selectedPathIds = useMemo(
    () => new Set(selectedPath?.map((n) => n.id) ?? []),
    [selectedPath]
  );
  const breadcrumbLabel = useMemo(() => {
    if (!selectedPath?.length) return null;
    return selectedPath.map((n) => n.name).join(' › ');
  }, [selectedPath]);

  const renderNode = (node: OpportunityFolderTreeNode, depth: number): React.ReactNode => {
    const isActive = selectedFolderId === node.id;
    const onSelectedBranch = selectedPathIds.has(node.id);
    const isAncestorOfSelection = onSelectedBranch && !isActive;
    const actionButtonVariant = isActive ? 'light' : 'outline-secondary';
    const rowClasses = [
      'opportunity-folder-item',
      'd-flex',
      'align-items-center',
      'justify-content-between',
      isActive ? 'opportunity-folder-item--selected' : '',
      isAncestorOfSelection ? 'opportunity-folder-item--ancestor' : '',
    ]
      .filter(Boolean)
      .join(' ');
    return (
      <React.Fragment key={node.id}>
      <ListGroup.Item
        action
        active={isActive}
        className={rowClasses}
        aria-current={isActive ? 'true' : undefined}
        style={{ paddingLeft: `${10 + depth * 20}px` }}
        onClick={() => {
          setOpenMenuId(null);
          onSelectFolder(node.id);
        }}
      >
        <span className="folder-name me-2 d-flex align-items-center gap-2">
          <i
            className={`bi folder-icon ${depth > 0 ? 'bi-folder2-open' : 'bi-folder2'}`}
            aria-hidden
          />
          <span className="folder-name-text">{node.name}</span>
        </span>
        {canEdit && (
          <div className="folder-actions-dropdown">
            <Button
              variant={actionButtonVariant}
              size="sm"
              aria-expanded={openMenuId === node.id}
              aria-label={`Ações da pasta ${node.name}`}
              onClick={(event) => {
                event.stopPropagation();
                setOpenMenuId((current) => (current === node.id ? null : node.id));
              }}
            >
              ...
            </Button>
            {openMenuId === node.id && (
              <div
                className="folder-actions-menu custom-folder-actions-menu"
                role="menu"
                onClick={(event) => {
                  event.stopPropagation();
                }}
              >
                <button type="button" className="folder-actions-item" onClick={() => { setOpenMenuId(null); onCreateFolder(node.id); }}>
                Nova subpasta
                </button>
                <button type="button" className="folder-actions-item" onClick={() => { setOpenMenuId(null); onRenameFolder(node); }}>
                Renomear
                </button>
                <button type="button" className="folder-actions-item" onClick={() => { setOpenMenuId(null); onMoveFolder(node); }}>
                Mover
                </button>
                <div className="folder-actions-divider" />
                <button type="button" className="folder-actions-item folder-actions-item-danger" onClick={() => { setOpenMenuId(null); onDeleteFolder(node); }}>
                Excluir
                </button>
              </div>
            )}
          </div>
        )}
      </ListGroup.Item>
      {node.children.map((child) => renderNode(child, depth + 1))}
    </React.Fragment>
  );
  };

  return (
    <div ref={containerRef}>
      <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
        <h6 className="mb-0">Pastas</h6>
        {canEdit && (
          <div className="d-flex gap-1 flex-wrap justify-content-end">
            {onApplyModuleDefaults && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => onApplyModuleDefaults(selectedFolderId ?? null)}
                title="Usa a lista definida em Oportunidades → Configuração do módulo (pastas na raiz)"
              >
                Aplicar conjunto padrão
              </Button>
            )}
            <Button variant="primary" size="sm" onClick={() => onCreateFolder(null)}>
              Nova pasta
            </Button>
          </div>
        )}
      </div>
      {breadcrumbLabel && (
        <div
          className="opportunity-folder-breadcrumb mb-2 px-1"
          title={breadcrumbLabel}
          aria-label={`Caminho da pasta selecionada: ${breadcrumbLabel}`}
        >
          <span className="opportunity-folder-breadcrumb-label">Localização</span>
          <span className="opportunity-folder-breadcrumb-path text-truncate d-block">{breadcrumbLabel}</span>
        </div>
      )}
      <ListGroup className="opportunity-folder-list">
        <ListGroup.Item
          action
          active={selectedFolderId === null || selectedFolderId === undefined}
          className={`opportunity-folder-root-item ${
            selectedFolderId === null || selectedFolderId === undefined
              ? 'opportunity-folder-item--selected'
              : ''
          }`}
          aria-current={selectedFolderId === null || selectedFolderId === undefined ? 'true' : undefined}
          onClick={() => {
            setOpenMenuId(null);
            onSelectFolder(null);
          }}
        >
          <span className="folder-name d-flex align-items-center gap-2">
            <i className="bi bi-archive folder-icon" aria-hidden />
            <span className="folder-name-text">{rootLabel}</span>
          </span>
        </ListGroup.Item>
        {visibleFolders.map((folder) => renderNode(folder, 0))}
      </ListGroup>
    </div>
  );
};

export default OpportunityFolderTree;
